import { contentBundleSchemaVersion, validateContentBundleStructure } from "@safesphere/contracts";
import type { ContentBundleEnvelope } from "@safesphere/contracts";

import { canonicalJson } from "./canonicalJson.js";
import { base64UrlToBytes, bytesToBase64Url } from "./encoding.js";
import { findTrustedContentKey } from "./trustStore.js";

export const applicationVersion = "0.0.0";
export const supportedBundleFeatures = new Set<string>();

export type BundleVerificationFailure =
  | "INVALID_STRUCTURE"
  | "INCOMPATIBLE_SCHEMA"
  | "INCOMPATIBLE_APP_VERSION"
  | "UNSUPPORTED_FEATURE"
  | "CORRUPTED_HASH"
  | "UNTRUSTED_KEY"
  | "INVALID_SIGNATURE";

export type BundleVerificationResult =
  | { readonly ok: true; readonly bundle: ContentBundleEnvelope }
  | { readonly ok: false; readonly reason: BundleVerificationFailure };

function parseVersion(version: string): readonly [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/u.exec(version);
  return match === null ? null : [Number(match[1]), Number(match[2]), Number(match[3])];
}

function isVersionAtLeast(current: string, minimum: string): boolean {
  const currentParts = parseVersion(current);
  const minimumParts = parseVersion(minimum);
  if (currentParts === null || minimumParts === null) return false;

  for (let index = 0; index < 3; index += 1) {
    const currentPart = currentParts[index] ?? 0;
    const minimumPart = minimumParts[index] ?? 0;
    if (currentPart !== minimumPart) return currentPart > minimumPart;
  }
  return true;
}

export async function hashBundleBody(
  body: ContentBundleEnvelope["body"],
): Promise<Uint8Array<ArrayBuffer>> {
  const bytes = new TextEncoder().encode(canonicalJson(body));
  return new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
}

export async function verifyContentBundle(candidate: unknown): Promise<BundleVerificationResult> {
  const validation = validateContentBundleStructure(candidate);
  if (!validation.valid) return { ok: false, reason: "INVALID_STRUCTURE" };

  const bundle = candidate as ContentBundleEnvelope;
  if (
    bundle.body.schemaVersion > contentBundleSchemaVersion ||
    bundle.body.compatibility.maximumSchemaVersion < bundle.body.schemaVersion
  ) {
    return { ok: false, reason: "INCOMPATIBLE_SCHEMA" };
  }
  if (!isVersionAtLeast(applicationVersion, bundle.body.compatibility.minimumAppVersion)) {
    return { ok: false, reason: "INCOMPATIBLE_APP_VERSION" };
  }
  if (
    bundle.body.compatibility.requiredFeatures.some(
      (feature) => !supportedBundleFeatures.has(feature),
    )
  ) {
    return { ok: false, reason: "UNSUPPORTED_FEATURE" };
  }

  const bodyHash = await hashBundleBody(bundle.body);
  if (bytesToBase64Url(bodyHash) !== bundle.integrity.hash) {
    return { ok: false, reason: "CORRUPTED_HASH" };
  }

  const trustedKey = findTrustedContentKey(bundle.signature.keyId);
  if (trustedKey === undefined) return { ok: false, reason: "UNTRUSTED_KEY" };

  let signatureValid: boolean;
  try {
    const publicKey = await crypto.subtle.importKey(
      "jwk",
      trustedKey.publicKey,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    signatureValid = await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      publicKey,
      base64UrlToBytes(bundle.signature.value),
      bodyHash,
    );
  } catch {
    return { ok: false, reason: "INVALID_SIGNATURE" };
  }

  return signatureValid ? { ok: true, bundle } : { ok: false, reason: "INVALID_SIGNATURE" };
}
